import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Form } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { ResourceConfigAddCard, ResourceConfigCard, formatVendorDeviceLabel } from '@/delivery-type-plugin/components/resourceConfig';

import { SharedMemoryConfigModal } from './SharedMemoryConfigModal';
import { SHARED_MEMORY_MODAL_CONFIG_FIELD, SHARED_MEMORY_RESOURCE_ICON } from './constants';
import { SHARED_MEMORY_FIELD } from './fieldKeys';
import styles from './index.module.less';
import {
  clampSharedMemoryConfigToMin,
  clearSharedMemoryFromForm,
  hasMeaningfulSharedMemoryConfig,
  parseSharedMemorySizeGi,
  readSharedMemoryFromFormValues,
} from './sharedMemoryForm';
import type { SharedMemoryFormValues } from './types';

export interface SharedMemoryResourceBlockProps {
  form: FormInstance;
  configField?: string;
  enabledField?: string;
  resourceEditMode?: string;
  /** 平台镜像约束的容量下限（Gi） */
  minGi?: number;
}

function buildSharedMemorySummaryItems(config?: SharedMemoryFormValues | null) {
  const sizeLimit = String(config?.sizeLimit ?? '').trim();
  if (!sizeLimit) return [];
  return [
    { label: '资源配置', value: sizeLimit },
    { label: '厂商设备', value: formatVendorDeviceLabel() },
  ];
}

/** 共享内存能力块：卡片 + Modal（可选，镜像预填后自动展示卡片） */
export function SharedMemoryResourceBlock({
  form,
  configField = SHARED_MEMORY_FIELD,
  enabledField = '_sharedMemoryEnabled',
  resourceEditMode,
  minGi,
}: SharedMemoryResourceBlockProps) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const isOrderFixed = resourceEditMode === 'order-fixed';
  const locked = isOrderFixed;

  const sharedMemoryConfig = Form.useWatch(configField, form);
  const sharedMemoryEnabledFlag = Form.useWatch(enabledField, form);

  const sharedMemoryEnabled =
    sharedMemoryEnabledFlag !== false &&
    (sharedMemoryEnabledFlag === true || hasMeaningfulSharedMemoryConfig(sharedMemoryConfig as SharedMemoryFormValues));

  useEffect(() => {
    if (form.getFieldValue(enabledField) === false) return;
    if (!hasMeaningfulSharedMemoryConfig(sharedMemoryConfig as SharedMemoryFormValues) || form.getFieldValue(enabledField) === true) {
      return;
    }
    form.setFieldValue(enabledField, true);
  }, [configField, enabledField, form, sharedMemoryConfig]);

  useEffect(() => {
    if (!minGi || minGi <= 0) return;
    const current = form.getFieldValue(configField) as SharedMemoryFormValues | undefined;
    if (!hasMeaningfulSharedMemoryConfig(current)) return;
    if (parseSharedMemorySizeGi(current?.sizeLimit) >= minGi) return;
    form.setFieldValue(configField, clampSharedMemoryConfigToMin(current, minGi));
  }, [configField, form, minGi, sharedMemoryConfig]);

  const resolvedConfig = useMemo(
    () => readSharedMemoryFromFormValues({ [configField]: sharedMemoryConfig }),
    [configField, sharedMemoryConfig],
  );

  const hasCardData = sharedMemoryEnabled && hasMeaningfulSharedMemoryConfig(resolvedConfig);
  const summaryItems = useMemo(() => buildSharedMemorySummaryItems(resolvedConfig), [resolvedConfig]);

  const modalInitialValues = useMemo(() => {
    if (!modalOpen) return undefined;
    return {
      [SHARED_MEMORY_MODAL_CONFIG_FIELD]: clampSharedMemoryConfigToMin(resolvedConfig, minGi),
    };
  }, [minGi, modalOpen, resolvedConfig]);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const handleModalOk = useCallback(
    (modalValues: Record<string, unknown>) => {
      const config = modalValues[SHARED_MEMORY_MODAL_CONFIG_FIELD] as SharedMemoryFormValues | undefined;
      form.setFieldValue(configField, config);
      form.setFieldValue(enabledField, hasMeaningfulSharedMemoryConfig(config));
      closeModal();
    },
    [closeModal, configField, enabledField, form],
  );

  const handleRemove = useCallback(() => {
    clearSharedMemoryFromForm(form, { field: configField });
    form.setFieldValue(enabledField, false);
  }, [configField, enabledField, form]);

  const addLabel = t('data-center:shared.memory');

  const renderCard = (hasError: boolean) =>
    hasCardData ? (
      <ResourceConfigCard
        resourceCode="SharedMemory"
        icon={SHARED_MEMORY_RESOURCE_ICON}
        title={addLabel}
        items={summaryItems}
        canEdit={!locked}
        canDelete={!locked}
        hasError={hasError}
        onEdit={openModal}
        onDelete={handleRemove}
      />
    ) : locked ? null : (
      <ResourceConfigAddCard
        resourceCode="SharedMemory"
        icon={SHARED_MEMORY_RESOURCE_ICON}
        title={hasError ? `${addLabel}（必填）` : addLabel}
        hasError={hasError}
        onAdd={openModal}
      />
    );

  return (
    <>
      <Form.Item shouldUpdate noStyle>
        {(_, formInstance) => renderCard(Boolean(formInstance.getFieldError(configField)?.message))}
      </Form.Item>

      <div className={styles.persistFields} aria-hidden>
        <Form.Item field={configField} hidden>
          <span />
        </Form.Item>
        <Form.Item field={enabledField} hidden triggerPropName="checked">
          <Checkbox />
        </Form.Item>
      </div>

      {modalOpen ? (
        <SharedMemoryConfigModal initialValues={modalInitialValues} onOk={handleModalOk} onCancel={closeModal} min={minGi} />
      ) : null}
    </>
  );
}
